"use client";

import { useEffect, useRef } from "react";

const LOGO_PATH = "/assets/sdtc-image/trang-chu/Logo/sdtc.webp";

interface NewsContentProps {
  content: string;
  className?: string;
}

export default function NewsContent({ content, className }: NewsContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const enforceLinkStyle = (link: HTMLAnchorElement) => {
      // Xóa màu inline cũ và gán màu chuẩn để tránh bị đen
      link.style.removeProperty("color");
      link.style.setProperty("color", "#265ff3", "important");
      link.style.removeProperty("text-decoration");
      link.style.setProperty("text-decoration", "underline");
      // Đảm bảo các thẻ con (strong, em, span...) kế thừa màu
      link.querySelectorAll("*").forEach((child) => {
        (child as HTMLElement).style.removeProperty("color");
        (child as HTMLElement).style.setProperty("color", "#265ff3", "important");
      });
    };

    // Xử lý các link trong nội dung: chuyển /tin-tuc/ thành /section/tin-tuc/
    const links = contentRef.current.querySelectorAll("a");
    links.forEach((link) => {
      const href = link.getAttribute("href");
      if (href && href.startsWith("/tin-tuc/")) {
        link.setAttribute("href", href.replace("/tin-tuc/", "/tin-tuc/"));
      } else if (href === "/tin-tuc") {
        link.setAttribute("href", "/tin-tuc");
      }
      enforceLinkStyle(link as HTMLAnchorElement);
    });

    const handleImageError = (img: HTMLImageElement) => {
      // Chỉ thay thế nếu chưa phải logo
      if (img.src !== LOGO_PATH && !img.src.includes(LOGO_PATH)) {
        img.src = LOGO_PATH;
        img.onerror = null; // Tránh loop vô hạn nếu logo cũng lỗi
      }
    };

    // Tìm tất cả các thẻ img trong nội dung
    const images = contentRef.current.querySelectorAll("img");

    images.forEach((img) => {
      // Kiểm tra nếu ảnh đã load và lỗi
      if (img.complete) {
        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
          handleImageError(img);
        }
      }

      // Thêm onError handler
      img.onerror = () => handleImageError(img);

      // Kiểm tra khi ảnh load xong
      img.onload = () => {
        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
          handleImageError(img);
        }
      };
    });

    // Sử dụng MutationObserver để theo dõi các ảnh và link được thêm vào sau
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            // Kiểm tra node mới có phải là img không
            if (element.tagName === "IMG") {
              const img = element as HTMLImageElement;
              img.onerror = () => handleImageError(img);
              if (img.complete && (img.naturalWidth === 0 || img.naturalHeight === 0)) {
                handleImageError(img);
              }
            }
            // Kiểm tra node mới có phải là link không
            if (element.tagName === "A") {
              const link = element as HTMLAnchorElement;
              const href = link.getAttribute("href");
              if (href && href.startsWith("/tin-tuc/")) {
                link.setAttribute("href", href.replace("/tin-tuc/", "/tin-tuc/"));
              } else if (href === "/tin-tuc") {
                link.setAttribute("href", "/tin-tuc");
              }
              enforceLinkStyle(link);
            }
            // Kiểm tra các img bên trong node mới
            const images = element.querySelectorAll("img");
            images.forEach((img) => {
              img.onerror = () => handleImageError(img);
              if (img.complete && (img.naturalWidth === 0 || img.naturalHeight === 0)) {
                handleImageError(img);
              }
            });
            // Kiểm tra các link bên trong node mới
            const links = element.querySelectorAll("a");
            links.forEach((link) => {
              const href = link.getAttribute("href");
              if (href && href.startsWith("/tin-tuc/")) {
                link.setAttribute("href", href.replace("/tin-tuc/", "/tin-tuc/"));
              } else if (href === "/tin-tuc") {
                link.setAttribute("href", "tin-tuc");
              }
              enforceLinkStyle(link as HTMLAnchorElement);
            });
          }
        });
      });
    });

    observer.observe(contentRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [content]);

  return (
    <div
      ref={contentRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}